import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

/**
 * Stops an in-app navigation (and a tab close) from silently discarding edits.
 *
 * The access matrix in particular had no protection at all: it held a full
 * permission draft in local state behind a single Save button, so clicking any
 * sidebar link threw the work away with no warning.
 */
export function UnsavedChangesGuard({
  when,
  title = 'Discard unsaved changes?',
  description = 'You have edits that have not been saved yet. Leaving this page will lose them.',
  confirmLabel = 'Discard and leave',
}: {
  when: boolean;
  title?: string;
  description?: string;
  confirmLabel?: string;
}) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      when && currentLocation.pathname !== nextLocation.pathname,
  );

  // Covers reloads and tab closes, which the router can't see.
  useEffect(() => {
    if (!when) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [when]);

  // A blocked navigation that is no longer relevant (the user saved, say)
  // should release itself rather than stay stuck.
  useEffect(() => {
    if (!when && blocker.state === 'blocked') blocker.reset();
  }, [when, blocker]);

  return (
    <ConfirmDialog
      open={blocker.state === 'blocked'}
      onOpenChange={(next) => {
        if (!next) blocker.reset?.();
      }}
      title={title}
      description={description}
      confirmLabel={confirmLabel}
      cancelLabel="Stay on this page"
      tone="destructive"
      onConfirm={() => blocker.proceed?.()}
    />
  );
}
